import React, { useState, useEffect } from "react";
import { Download, ChevronDown, ChevronUp } from "react-feather";
import { Button, Dropdown, Menu } from "antd";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment-timezone";
import axios from "axios";
import Swal from "sweetalert2";
import exportService from "../../utils/exportService";
import PrintReportPreview from "../../reports/PrintReportPreview";
import { useTranslation } from "react-i18next";

const ShiftEndSummary = () => {
  const { t } = useTranslation();
  const apiURL = process.env.REACT_APP_API_URL || "http://localhost:4000";
  const currency = localStorage.getItem("currencycode") || "₹";

  const [selectedDate, setSelectedDate] = useState(() => {
    const businessDate = localStorage.getItem("business_date");
    const today = moment().tz("Asia/Kolkata").startOf("day");
    const yesterday = today.clone().subtract(1, "days");
    return businessDate && moment(businessDate).isValid()
      ? moment(businessDate).tz("Asia/Kolkata").subtract(1, "days").toDate()
      : yesterday.toDate();
  });
  const [previewVisible, setPreviewVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    frontOfficeActivities: true,
    financialSummary: true,
    shiftNotes: true,
  });
  const [shiftDetails] = useState({
    shiftName: "Morning Shift",
    startTime: "2025-04-10 08:00 AM",
    endTime: "2025-04-10 04:00 PM",
    staffName: "John Doe",
  });
  const [reportData, setReportData] = useState({
    summary: {
      totalCheckIns: 0,
      totalCheckOuts: 0,
      occupancyPercentage: 0,
      totalRevenue: 0,
    },
    frontOfficeActivities: {
      guestMovements: {
        checkIns: 0,
        checkOuts: 0,
        noShows: 0,
        cancellations: 0,
        stayover: 0,
      },
      roomStatus: {
        available: 0,
        occupied: 0,
        outOfOrder: 0,
      },
      guestRequests: {
        made: 0,
        resolved: 0,
      },
    },
    financialSummary: {
      payments: [
        { type: "Cash", amount: 0 },
        { type: "Credit Card", amount: 0 },
        { type: "Online", amount: 0 },
      ],
      refundsAdjustments: 0,
      pendingPayments: 0,
    },
    shiftNotes: [],
  });

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const companyid = localStorage.getItem("companyid");

      // Validate token and companyid
      if (!token) {
        throw new Error("No authentication token found. Please log in again.");
      }
      if (!companyid || isNaN(parseInt(companyid))) {
        throw new Error("Valid companyid is required.");
      }
      if (!moment(selectedDate).isValid()) {
        throw new Error("Invalid business date.");
      }

      const businessDate = moment(selectedDate).tz("Asia/Kolkata").format("YYYY-MM-DD");

      // Debug: Log token and request details
      console.log("Token:", token);
      console.log("Company ID:", companyid);
      console.log("Request URL:", `${apiURL}/reports-daily/day-end-summary?businessDate=${businessDate}`);

      const response = await axios.get(`${apiURL}/reports-daily/day-end-summary`, {
        params: { businessDate },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("API Response:", response.data);

      if (response.data.success && response.data.report) {
        const { report } = response.data;
        const summary = typeof report.summary === "string" ? JSON.parse(report.summary) : report.summary;

        // Map API response to reportData structure
        setReportData({
          summary: {
            totalCheckIns: summary.checked_in || 0,
            totalCheckOuts: summary.checked_out || 0,
            occupancyPercentage:
              report.occupied_rooms && report.occupied_rooms + report.vacant_rooms > 0
                ? (report.occupied_rooms / (report.occupied_rooms + report.vacant_rooms)) * 100
                : 0,
            totalRevenue: summary.room_revenue || 0,
          },
          frontOfficeActivities: {
            guestMovements: {
              checkIns: summary.checked_in || 0,
              checkOuts: summary.checked_out || 0,
              noShows: summary.no_show || 0,
              cancellations: summary.cancelled || 0,
              stayover: summary.stayover || 0,
            },
            roomStatus: {
              available: report.vacant_rooms || 0,
              occupied: report.occupied_rooms || 0,
              outOfOrder: report.maintenance_rooms || 0,
            },
            guestRequests: {
              made: 0, // Placeholder, as API doesn't provide this
              resolved: 0, // Placeholder
            },
          },
          financialSummary: {
            payments: [
              { type: "Cash", amount: summary.cash_collection || 0 },
              { type: "Credit Card", amount: summary.bank_card_collection || 0 },
              { type: "Online", amount: (summary.upi_collection || 0) + (summary.bank_transfer_collection || 0) },
            ],
            refundsAdjustments: summary.expenses || 0,
            pendingPayments: 0, // Placeholder
          },
          shiftNotes: report.notes ? [{ note: report.notes }] : [],
        });
      } else {
        throw new Error(response.data.message || "Failed to fetch day-end summary.");
      }
    } catch (error) {
      console.error("Error fetching day-end summary:", error);
      Swal.fire({
        title: t("error"),
        text: error.response?.data?.message || t("failed_fetch_day_end_summary"),
        icon: "error",
      });
      setReportData({
        summary: {
          totalCheckIns: 0,
          totalCheckOuts: 0,
          occupancyPercentage: 0,
          totalRevenue: 0,
        },
        frontOfficeActivities: {
          guestMovements: {
            checkIns: 0,
            checkOuts: 0,
            noShows: 0,
            cancellations: 0,
            stayover: 0,
          },
          roomStatus: {
            available: 0,
            occupied: 0,
            outOfOrder: 0,
          },
          guestRequests: {
            made: 0,
            resolved: 0,
          },
        },
        financialSummary: {
          payments: [
            { type: "Cash", amount: 0 },
            { type: "Credit Card", amount: 0 },
            { type: "Online", amount: 0 },
          ],
          refundsAdjustments: 0,
          pendingPayments: 0,
        },
        shiftNotes: [],
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount and when selectedDate changes
  useEffect(() => {
    fetchData();
  }, [moment(selectedDate).format("YYYY-MM-DD")]);

  // Handle date change for prev/next buttons
  const handleDateChange = (action) => {
    let newDate;
    if (action === "prev") {
      newDate = moment(selectedDate).subtract(1, "days").toDate();
    } else if (action === "next") {
      newDate = moment(selectedDate).add(1, "days").toDate();
    }
    if (moment(newDate).isAfter(moment().tz("Asia/Kolkata").subtract(1, "days"))) {
      Swal.fire({
        title: t("error"),
        text: t("date_cannot_be_current_or_future"),
        icon: "error",
      });
      return;
    }
    setSelectedDate(newDate);
  };

  // Toggle section visibility
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Generate export data
  const generateExportData = () => {
    const data = [];
    data.push({ section: t("summary"), metric: t("total_check_ins"), value: reportData.summary.totalCheckIns });
    data.push({ section: t("summary"), metric: t("total_check_outs"), value: reportData.summary.totalCheckOuts });
    data.push({ section: t("summary"), metric: t("occupancy_percentage"), value: `${reportData.summary.occupancyPercentage.toFixed(2)}%` });
    data.push({ section: t("summary"), metric: t("total_revenue"), value: `${currency}${reportData.summary.totalRevenue.toFixed(2)}` });

    data.push({ section: t("front_office_activities_guest_movements"), metric: t("check_ins"), value: reportData.frontOfficeActivities.guestMovements.checkIns });
    data.push({ section: t("front_office_activities_guest_movements"), metric: t("check_outs"), value: reportData.frontOfficeActivities.guestMovements.checkOuts });
    data.push({ section: t("front_office_activities_guest_movements"), metric: t("no_shows"), value: reportData.frontOfficeActivities.guestMovements.noShows });
    data.push({ section: t("front_office_activities_guest_movements"), metric: t("cancellations"), value: reportData.frontOfficeActivities.guestMovements.cancellations });
    data.push({ section: t("front_office_activities_guest_movements"), metric: t("stayovers"), value: reportData.frontOfficeActivities.guestMovements.stayover });

    data.push({ section: t("front_office_activities_room_status"), metric: t("available"), value: reportData.frontOfficeActivities.roomStatus.available });
    data.push({ section: t("front_office_activities_room_status"), metric: t("occupied"), value: reportData.frontOfficeActivities.roomStatus.occupied });
    data.push({ section: t("front_office_activities_room_status"), metric: t("out_of_order"), value: reportData.frontOfficeActivities.roomStatus.outOfOrder });

    //data.push({ section: t("front_office_activities_guest_requests"), metric: t("made"), value: reportData.frontOfficeActivities.guestRequests.made });
    //data.push({ section: t("front_office_activities_guest_requests"), metric: t("resolved"), value: reportData.frontOfficeActivities.guestRequests.resolved });

    reportData.financialSummary.payments.forEach((payment) => {
      data.push({ section: t("financial_summary_payments"), metric: payment.type, value: `${currency}${payment.amount.toFixed(2)}` });
    });

    data.push({ section: t("financial_summary"), metric: t("refunds_adjustments"), value: `${currency}${reportData.financialSummary.refundsAdjustments.toFixed(2)}` });
    data.push({ section: t("financial_summary"), metric: t("pending_payments"), value: `${currency}${reportData.financialSummary.pendingPayments.toFixed(2)}` });

    reportData.shiftNotes.forEach((note, index) => {
      data.push({ section: t("shift_notes"), metric: `Note ${index + 1}`, value: note.note });
    });

    return data;
  };

  const exportHeaders = [
    { key: "section", label: t("section") },
    { key: "metric", label: t("metric") },
    { key: "value", label: t("value") },
  ];

  // Download handlers
  const handleDownloadCSV = () => {
    const exportData = generateExportData();
    if (!exportData.length) {
      Swal.fire({
        title: t("error"),
        text: t("no_data_available_for_export"),
        icon: "error",
      });
      return;
    }
    const filename = `shift_end_summary_${moment(selectedDate).format("YYYY-MM-DD")}`;
    exportService.exportToCSV(exportData, exportHeaders, `${filename}.csv`);
  };

  const handleDownloadXLS = () => {
    const exportData = generateExportData();
    if (!exportData.length) {
      Swal.fire({
        title: t("error"),
        text: t("no_data_available_for_export"),
        icon: "error",
      });
      return;
    }
    const filename = `shift_end_summary_${moment(selectedDate).format("YYYY-MM-DD")}`;
    exportService.exportToXLS(exportData, exportHeaders, `${filename}.xlsx`, t("shift_end_summary"));
  };

  const handleDownloadPDF = () => {
    const exportData = generateExportData();
    if (!exportData.length) {
      Swal.fire({
        title: t("error"),
        text: t("no_data_available_for_export"),
        icon: "error",
      });
      return;
    }
    const filename = `shift_end_summary_${moment(selectedDate).format("YYYY-MM-DD")}`;
    exportService.exportToPDF(
      exportData,
      exportHeaders,
      t("shift_end_summary"),
      `${filename}.pdf`,
      false,
      true,
      [],
      []
    );
  };

  const handlePreview = () => {
    const exportData = generateExportData();
    if (!exportData.length) {
      Swal.fire({
        title: t("error"),
        text: t("no_data_available_for_preview"),
        icon: "error",
      });
      return;
    }
    setPreviewVisible(true);
  };

  const downloadMenu = (
    <Menu>
      <Menu.Item key="csv" onClick={handleDownloadCSV}>{t("export_csv")}</Menu.Item>
      <Menu.Item key="xls" onClick={handleDownloadXLS}>{t("export_xls")}</Menu.Item>
      <Menu.Item key="preview" onClick={handlePreview}>{t("preview")}</Menu.Item>
    </Menu>
  );

  const activityStats = [
    { label: t("total_check_ins"), value: reportData.summary.totalCheckIns, color: "#28a745" },
    { label: t("total_check_outs"), value: reportData.summary.totalCheckOuts, color: "#007bff" },
    { label: t("occupancy_percentage"), value: `${reportData.summary.occupancyPercentage.toFixed(2)}%`, color: "#17a2b8" },
    { label: t("total_revenue"), value: `${currency}${reportData.summary.totalRevenue.toFixed(2)}`, color: "#dc3545" },
    { label: t("tax_collection"), value: `${currency}0.00`, color: "#ffc107" }, // Placeholder
    { label: t("net_revenue"), value: `${currency}${reportData.summary.totalRevenue.toFixed(2)}`, color: "#28a745" }, // Placeholder
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="shift-end-summary-container">
          <div className="page-header page-top-icons mb-4 px-3 py-2 bg-white border rounded">
            <div className="row align-items-center flex-nowrap">
              <div className="col-auto d-flex align-items-center gap-3">
                <h2
                  className="room-title mb-0"
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    width: "400px",
                    borderBottom: "2px solid #007bff",
                    paddingBottom: "4px",
                  }}
                >
                  {t("shift_end_summary")}
                </h2>
                <div className="d-flex align-items-center">
                  <Button
                    onClick={() => handleDateChange("prev")}
                    className="btn btn-outline-primary btn-sm rounded-circle me-2"
                    style={{ width: "34px", height: "34px" }}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </Button>
<DatePicker
  selected={selectedDate}
  onChange={(date) => {
    const businessDate = localStorage.getItem("business_date")
      ? moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").toDate()
      : moment().tz("Asia/Kolkata").subtract(1, "days").toDate();
    const maxAllowedDate = moment(businessDate).subtract(1, "days").toDate();

    if (moment(date).isAfter(moment(maxAllowedDate)) || moment(date).isSame(moment(businessDate), 'day')) {
      Swal.fire({
        title: t("error"),
        text: t("date_cannot_be_current_or_beyond_business_date"),
        icon: "error",
      });
      return;
    }
    setSelectedDate(date);
  }}
  dateFormat="dd MMM yyyy"
  maxDate={localStorage.getItem("business_date")
    ? moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "days").toDate()
    : moment().tz("Asia/Kolkata").subtract(2, "days").toDate()}
  placeholderText={t("select_date")}
  className="form-control text-center fw-bold"
  style={{ height: "38px", fontSize: "15px", width: "220px" }}
/>

<Button
  onClick={() => handleDateChange("next")}
  className="btn btn-outline-primary btn-sm rounded-circle ms-2"
  style={{ width: "34px", height: "34px" }}
  disabled={
    moment(selectedDate).isSame(
      localStorage.getItem("business_date")
        ? moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "days").toDate()
        : moment().tz("Asia/Kolkata").subtract(2, "days").toDate(),
      'day'
    )
  }
>
  <i className="fas fa-chevron-right"></i>
</Button>
                </div>
              </div>
              <div className="col-auto ms-auto d-flex align-items-center gap-3">
                <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight">
                  <Button type="primary" className="d-flex align-items-center" style={{ height: "38px" }}>
                    <Download size={16} className="me-2" /> {t("download")}
                  </Button>
                </Dropdown>
              </div>
            </div>
            <div style={{ fontSize: "14px", color: "#6c757d", fontFamily: "'Roboto', sans-serif", marginTop: "5px" }}></div>
          </div>

          {loading ? (
            <div className="text-center my-5">
              <div className="loader">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          ) : (
            <>

<div className="row g-4 mb-4" style={{ padding: "5px 10px" }}>
  {activityStats.map((stat, index) => (
    <div
      key={index}
      className="col-md-2 col-6 stats-box"
      style={{
        margin: "0 15px",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        padding: "10px",
        background: "#fff",
      }}
    >
      <div
        className="stats-border"
style={{ backgroundColor: stat.color }}
      ></div>
      <div className="stats-content">
        <h2 style={{ fontSize: "1.5rem", margin: 0, fontWeight: 600, color: "#666" }}>{stat.value}</h2>
        <p
          style={{
            margin: 0,
            color: "#666",
            fontSize: "0.9rem",
            textTransform: "capitalize",
          }}
        >
          {stat.label}
        </p>
      </div>
    </div>
  ))}
</div>


              <div className="mb-4">
                <div
                  className="d-flex justify-content-between align-items-center p-3"
                  style={{
                    backgroundColor: "#e6f4ea",
                    borderRadius: "8px 8px 0 0",
                    cursor: "pointer",
                    border: "1px solid #ced4da",
                    borderBottom: "none",
                  }}
                  onClick={() => toggleSection("frontOfficeActivities")}
                >
                  <h3 style={{ fontSize: "20px", fontWeight: "bold", margin: 0, color: "#1a3c34", fontFamily: "'Roboto', sans-serif" }}>
                    {t("front_office_activities")}
                  </h3>
                  {expandedSections.frontOfficeActivities ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
                {expandedSections.frontOfficeActivities && (
                  <div
                    style={{
                      backgroundColor: "#fff",
                      borderRadius: "0 0 8px 8px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                      padding: "20px",
                      border: "1px solid #ced4da",
                      borderTop: "none",
                    }}
                  >
                    <h4 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", fontFamily: "'Roboto', sans-serif", color: "#333" }}>
                      {t("guest_movements")}
                    </h4>
                    <table className="table table-bordered mb-4 uniform-table">
                      <thead style={{ backgroundColor: "#f8f9fa", fontWeight: "700", fontSize: "15px", fontFamily: "'Roboto', sans-serif" }}>
                        <tr>
                          <th>{t("activity")}</th>
                          <th>{t("count")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ fontSize: "14px", fontFamily: "'Roboto', sans-serif" }}>
                          <td>{t("check_ins")}</td>
                          <td>{reportData.frontOfficeActivities.guestMovements.checkIns}</td>
                        </tr>
                        <tr style={{ fontSize: "14px", fontFamily: "'Roboto', sans-serif" }}>
                          <td>{t("check_outs")}</td>
                          <td>{reportData.frontOfficeActivities.guestMovements.checkOuts}</td>
                        </tr>
                        <tr style={{ fontSize: "14px", fontFamily: "'Roboto', sans-serif" }}>
                          <td>{t("no_shows")}</td>
                          <td>{reportData.frontOfficeActivities.guestMovements.noShows}</td>
                        </tr>
                        <tr style={{ fontSize: "14px", fontFamily: "'Roboto', sans-serif" }}>
                          <td>{t("cancellations")}</td>
                          <td>{reportData.frontOfficeActivities.guestMovements.cancellations}</td>
                        </tr>
                        <tr style={{ fontSize: "14px", fontFamily: "'Roboto', sans-serif" }}>
                          <td>{t("stayovers")}</td>
                          <td>{reportData.frontOfficeActivities.guestMovements.stayover}</td>
                        </tr>
                      </tbody>
                    </table>

                    <h4 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", fontFamily: "'Roboto', sans-serif", color: "#333" }}>
                      {t("room_status")}
                    </h4>
                    <table className="table table-bordered mb-4 uniform-table">
                      <thead style={{ backgroundColor: "#f8f9fa", fontWeight: "700", fontSize: "15px", fontFamily: "'Roboto', sans-serif" }}>
                        <tr>
                          <th>{t("status")}</th>
                          <th>{t("count")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ fontSize: "14px", fontFamily: "'Roboto', sans-serif" }}>
                          <td>{t("available")}</td>
                          <td>{reportData.frontOfficeActivities.roomStatus.available}</td>
                        </tr>
                        <tr style={{ fontSize: "14px", fontFamily: "'Roboto', sans-serif" }}>
                          <td>{t("occupied")}</td>
                          <td>{reportData.frontOfficeActivities.roomStatus.occupied}</td>
                        </tr>
                        <tr style={{ fontSize: "14px", fontFamily: "'Roboto', sans-serif" }}>
                          <td>{t("out_of_order")}</td>
                          <td>{reportData.frontOfficeActivities.roomStatus.outOfOrder}</td>
                        </tr>
                      </tbody>
                    </table>

                  </div>
                )}
              </div>

              <div className="mb-4">
                <div
                  className="d-flex justify-content-between align-items-center p-3"
                  style={{
                    backgroundColor: "#e6f4ea",
                    borderRadius: "8px 8px 0 0",
                    cursor: "pointer",
                    border: "1px solid #ced4da",
                    borderBottom: "none",
                  }}
                  onClick={() => toggleSection("financialSummary")}
                >
                  <h3 style={{ fontSize: "20px", fontWeight: "bold", margin: 0, color: "#1a3c34", fontFamily: "'Roboto', sans-serif" }}>
                    {t("financial_summary")}
                  </h3>
                  {expandedSections.financialSummary ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
                {expandedSections.financialSummary && (
                  <div
                    style={{
                      backgroundColor: "#fff",
                      borderRadius: "0 0 8px 8px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                      padding: "20px",
                      border: "1px solid #ced4da",
                      borderTop: "none",
                    }}
                  >
                    <h4 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", fontFamily: "'Roboto', sans-serif", color: "#333" }}>
                      {t("payments")}
                    </h4>
                    <table className="table table-bordered mb-4 uniform-table">
                      <thead style={{ backgroundColor: "#f8f9fa", fontWeight: "700", fontSize: "15px", fontFamily: "'Roboto', sans-serif" }}>
                        <tr>
                          <th>{t("payment_type")}</th>
                          <th>{t("amount")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.financialSummary.payments.map((payment, index) => (
                          <tr key={index} style={{ fontSize: "14px", fontFamily: "'Roboto', sans-serif" }}>
                            <td>{payment.type}</td>
                            <td>{currency}{payment.amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <h4 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px", fontFamily: "'Roboto', sans-serif", color: "#333" }}>
                      {t("refunds_adjustments_and_pending_payments")}
                    </h4>
                    <table className="table table-bordered uniform-table">
                      <thead style={{ backgroundColor: "#f8f9fa", fontWeight: "700", fontSize: "15px", fontFamily: "'Roboto', sans-serif" }}>
                        <tr>
                          <th>{t("category")}</th>
                          <th>{t("amount")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ fontSize: "14px", fontFamily: "'Roboto', sans-serif" }}>
                          <td>{t("refunds_adjustments")}</td>
                          <td>{currency}{reportData.financialSummary.refundsAdjustments.toFixed(2)}</td>
                        </tr>
                        <tr style={{ fontSize: "14px", fontFamily: "'Roboto', sans-serif" }}>
                          <td>{t("pending_payments")}</td>
                          <td>{currency}{reportData.financialSummary.pendingPayments.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <div
                  className="d-flex justify-content-between align-items-center p-3"
                  style={{
                    backgroundColor: "#e6f4ea",
                    borderRadius: "8px 8px 0 0",
                    cursor: "pointer",
                    border: "1px solid #ced4da",
                    borderBottom: "none",
                  }}
                  onClick={() => toggleSection("shiftNotes")}
                >
                  <h3 style={{ fontSize: "20px", fontWeight: "bold", margin: 0, color: "#1a3c34", fontFamily: "'Roboto', sans-serif" }}>
                    {t("shift_notes")}
                  </h3>
                  {expandedSections.shiftNotes ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
                {expandedSections.shiftNotes && (
                  <div
                    style={{
                      backgroundColor: "#fff",
                      borderRadius: "0 0 8px 8px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                      padding: "20px",
                      border: "1px solid #ced4da",
                      borderTop: "none",
                    }}
                  >
                    <table className="table table-bordered uniform-table single-column-table">
                      <thead style={{ backgroundColor: "#f8f9fa", fontWeight: "700", fontSize: "15px", fontFamily: "'Roboto', sans-serif" }}>
                        <tr>
                          <th>{t("notes")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.shiftNotes.length > 0 ? (
                          reportData.shiftNotes.map((note, index) => (
                            <tr key={index} style={{ fontSize: "14px", fontFamily: "'Roboto', sans-serif" }}>
                              <td>{note.note}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td className="text-center">{t("no_notes_for_this_shift")}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div
                className="p-3 text-end"
                style={{
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                }}
              >
                <h3 style={{ fontSize: "20px", fontWeight: "bold", margin: 0, color: "#1a3c34", fontFamily: "'Roboto', sans-serif" }}>
                  {t("total_revenue")}: {currency}{reportData.summary.totalRevenue.toFixed(2)}
                </h3>
                <div style={{ fontSize: "14px", color: "#6c757d", fontFamily: "'Roboto', sans-serif" }}>
                  {t("report_generated")}: {moment().format("DD MMM YYYY, hh:mm A")}
                </div>
              </div>

              <PrintReportPreview
                visible={previewVisible}
                onClose={() => setPreviewVisible(false)}
                reportTitle={`${t("shift_end_summary")} - ${moment(selectedDate).format("DD MMM YYYY")}`}
                propertyName=""
                data={generateExportData()}
                headers={exportHeaders}
                footerData={[]}
                totalColumns={[]}
                apiEndpoint="transactions/generate-pdf"
                apiUrl={apiURL}
              />
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .stats-box {
          transition: all 0.3s ease;
        }
        .stats-box:hover {
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }
        .stats-content h2 {
          color: #333;
        }
        .stats-content p {
          text-transform: lowercase;
        }
        .uniform-table th,
        .uniform-table td {
          width: 50%;
          padding: 8px 12px;
          vertical-align: middle;
          border: 1px solid #ced4da;
        }
        .single-column-table th,
        .single-column-table td {
          width: 100%;
          padding: 8px 12px;
          vertical-align: middle;
          border: 1px solid #ced4da;
        }
        .table tbody tr:nth-child(odd) {
          background-color: #fff;
        }
        .table tbody tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .table tbody tr:hover {
          background-color: #f1f3f5;
          transition: background-color 0.2s;
        }
        .react-datepicker-wrapper {
          display: inline-block;
          width: 220px;
        }
        .react-datepicker__input-container input {
          height: 38px;
          font-size: 15px;
          border: 1px solid #d9d9d9;
          border-radius: 4px;
          padding: 6px 12px;
          background-color: #ffffff;
          width: 100%;
          text-align: center;
          font-weight: bold;
        }
        .loader {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .dot {
          width: 10px;
          height: 10px;
          background-color: #007bff;
          border-radius: 50%;
          margin: 0 5px;
          animation: dot-bounce 1.4s infinite ease-in-out both;
        }
        .dot:nth-child(1) {
          animation-delay: -0.32s;
        }
        .dot:nth-child(2) {
          animation-delay: -0.16s;
        }
        @keyframes dot-bounce {
          0%,
          80%,
          100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default ShiftEndSummary;
